import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bell, MessageSquareText, Timer, CalendarClock, Receipt, Search, CheckCheck,
  Loader2, Inbox, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { RATE_META } from "@/lib_app/api";
import Avatar from "@/components/app/Avatar";
import NotificationQuickView from "@/components/app/NotificationQuickView";

const TYPES = [
  { key: "all", label: "Semua", icon: Bell },
  { key: "note", label: "Catatan Tim", icon: MessageSquareText },
  { key: "prospect_overdue", label: "Prospek Telat FU", icon: Timer },
  { key: "project_overdue", label: "Proyek Lewat Deadline", icon: CalendarClock },
  { key: "invoice_due", label: "Invoice Jatuh Tempo", icon: Receipt },
];

const TYPE_META = {
  note: { icon: MessageSquareText, ring: "bg-[#E5EEFA] text-[#3B4A63]" },
  prospect_overdue: { icon: Timer, ring: "bg-[#FFE89A] text-[#7A6633]" },
  project_overdue: { icon: CalendarClock, ring: "bg-[#FBD9DA] text-[#A94B4A]" },
  invoice_due: { icon: Receipt, ring: "bg-[#C9F0D6] text-[#1E6B39]" },
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (diff === 0) return "Hari ini";
  if (diff === 1) return "Kemarin";
  if (diff === -1) return "Besok";
  if (diff < -1) return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  if (diff < 7) return `${diff} hari lalu`;
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
};

const timeLabel = (iso) => new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });

export default function NotificationCenter() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [type, setType] = useState(params.get("type") || "all");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [quickView, setQuickView] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async (nextPage = 1, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const { data } = await api.get("/notifications/feed", {
        params: { type, status, q: debouncedQ || undefined, page: nextPage, per_page: 20 },
      });
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setCounts(data.counts || {});
      setHasMore(!!data.has_more);
      setTotal(data.total || 0);
      setPage(nextPage);
    } catch {
      toast.error("Gagal memuat notifikasi");
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [type, status, debouncedQ]);

  useEffect(() => { load(1, false); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (type !== "all") next.set("type", type);
    if (status !== "all") next.set("status", status);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status]);

  const groups = useMemo(() => {
    const out = [];
    items.forEach((it) => {
      const key = dayLabel(it.created_at);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(it);
      else out.push({ key, items: [it] });
    });
    return out;
  }, [items]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read_all");
      toast.success("Semua catatan ditandai dibaca");
      load(1, false);
    } catch { toast.error("Gagal menandai"); }
  };

  const openItem = async (it) => {
    if (it.type === "note") {
      if (!it.read) {
        try { await api.post(`/notifications/notes/${it.ref_id}/read`); } catch { /* noop */ }
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, read: true } : x)));
      }
      setQuickView({ prospectId: it.link.id, replyTo: it.actor_id, noteId: it.ref_id });
      return;
    }
    if (it.link?.kind === "prospect") navigate(`/prospects?open=${it.link.id}`);
    if (it.link?.kind === "project") navigate(`/projects?open=${it.link.id}`);
    if (it.link?.kind === "invoice") navigate(`/invoices?open=${it.link.id}`);
  };

  return (
    <div className="space-y-5" data-testid="notification-center">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <span className="text-[10px] font-bold text-[#A0A3A8] uppercase tracking-[0.18em]">Pusat Notifikasi</span>
          <h1 className="text-3xl md:text-4xl font-bold text-[#090808] tracking-tight mt-1">Notifikasi</h1>
          <p className="text-xs text-[#626061] mt-1">
            {total} item{counts.unread ? ` · ${counts.unread} belum ditindaklanjuti` : ""}
          </p>
        </div>
        <button
          onClick={markAllRead} data-testid="notif-center-mark-all"
          className="h-10 px-4 rounded-full bg-white border border-[#DCE0E7] hover:border-[#3B4A63] text-xs font-bold text-[#090808] inline-flex items-center gap-1.5 self-start"
        >
          <CheckCheck className="w-3.5 h-3.5" /> Tandai catatan dibaca
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#DCE0E7] card-soft p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="notif-center-type-filters">
          {TYPES.map((t) => {
            const Ico = t.icon;
            const on = type === t.key;
            const n = counts[t.key];
            return (
              <button
                key={t.key} onClick={() => setType(t.key)} data-testid={`notif-filter-${t.key}`}
                className={`h-9 px-3.5 rounded-full text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${
                  on ? "bg-[#090808] text-white border-[#090808]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
                }`}
              >
                <Ico className="w-3.5 h-3.5" /> {t.label}
                {n > 0 && (
                  <span className={`min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${on ? "bg-white/25 text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>{n}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#A0A3A8] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="notif-center-search"
              placeholder="Cari pelanggan, catatan, nomor invoice…"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#DCE0E7] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {[["all", "Semua"], ["unread", "Belum Dibaca"]].map(([v, l]) => (
              <button
                key={v} onClick={() => setStatus(v)} data-testid={`notif-status-${v}`}
                className={`h-10 px-4 rounded-full text-xs font-bold border transition-colors ${
                  status === v ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-[#DCE0E7] p-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#A0A3A8]" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#DCE0E7] py-16 text-center" data-testid="notif-center-empty">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-[#F4F5F7] flex items-center justify-center text-[#A0A3A8]">
            <Inbox className="w-5 h-5" />
          </div>
          <div className="text-sm font-bold text-[#090808] mt-3">Tidak ada notifikasi</div>
          <div className="text-xs text-[#A0A3A8] mt-1">Semua sudah ditindaklanjuti untuk filter ini.</div>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key} data-testid={`notif-group-${g.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold text-[#A0A3A8] uppercase tracking-[0.16em]">{g.key}</span>
                <span className="h-px flex-1 bg-[#DCE0E7]" />
                <span className="text-[10px] font-semibold text-[#A0A3A8]">{g.items.length}</span>
              </div>
              <div className="space-y-2">
                {g.items.map((it) => {
                  const meta = TYPE_META[it.type] || TYPE_META.note;
                  const Ico = meta.icon;
                  const rate = it.rate ? RATE_META[it.rate] : null;
                  return (
                    <button
                      key={it.id} onClick={() => openItem(it)} data-testid={`notif-item-${it.id}`}
                      className={`w-full text-left bg-white rounded-2xl border p-4 flex items-start gap-3 transition-colors hover:border-[#3B4A63] ${
                        it.read ? "border-[#DCE0E7]" : "border-[#B0C7EA] bg-[#FAFBFD]"
                      }`}
                    >
                      {it.type === "note" && it.actor_picture ? (
                        <Avatar name={it.actor_name} picture={it.actor_picture} size="md" />
                      ) : (
                        <span className={`w-10 h-10 rounded-2xl shrink-0 inline-flex items-center justify-center ${meta.ring}`}>
                          <Ico className="w-4 h-4" />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-[#090808]">{it.title}</span>
                          {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B7D]" />}
                          {rate && <span className={`w-4 h-4 rounded-full ${rate.pill} text-white text-[9px] font-bold inline-flex items-center justify-center`}>{rate.letter}</span>}
                        </div>
                        <div className="text-xs font-semibold text-[#3B4A63] mt-0.5 truncate">{it.subtitle}</div>
                        {it.body && <div className="text-xs text-[#626061] mt-1 line-clamp-2">{it.body}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0 text-[#A0A3A8]">
                        {it.type === "note" && <span className="text-[10px] font-semibold tabular-nums">{timeLabel(it.created_at)}</span>}
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {hasMore && (
            <button
              onClick={() => load(page + 1, true)} disabled={loadingMore} data-testid="notif-center-load-more"
              className="w-full h-11 rounded-full bg-white border border-[#DCE0E7] hover:border-[#3B4A63] text-sm font-bold text-[#090808] inline-flex items-center justify-center gap-2"
            >
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Muat lebih banyak ({total - items.length} tersisa)
            </button>
          )}
        </div>
      )}

      {quickView && (
        <NotificationQuickView
          prospectId={quickView.prospectId}
          replyTo={quickView.replyTo}
          noteId={quickView.noteId}
          onClose={() => setQuickView(null)}
        />
      )}
    </div>
  );
}
